// API Base URL - PHP Backend için
import { logger } from '../utils/logger';
// Development: http://localhost/backend/api (veya mevcut domain/backend/api)
// Production: Dinamik olarak mevcut domain'e göre belirlenir
// Local: http://localhost:8001 (local PHP server)
const resolveDefaultApiUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl && envUrl.trim() !== '') {
    return envUrl.trim();
  }

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const port = window.location.port;
    
    // PHP backend: /backend/api yolu ile erişilir
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost/backend/api';
    }
    
    // Production: Mevcut domain ve path'e göre otomatik belirle
    // PHP backend API route'ları /backend/api ile başlar
    // wepra.kayasoft.tr -> https://wepra.kayasoft.tr/backend/api
    const protocol = window.location.protocol;
    const host = window.location.host;
    return `${protocol}//${host}/backend/api`;
  }

  return 'https://wepra.kayasoft.tr/backend/api';
};

const API_BASE_URL = resolveDefaultApiUrl();

// Token geçerliliğini kontrol et (JWT token formatı)
// JWT token'ları decode edip expiry zamanını kontrol eder
function isTokenExpired(token: string): boolean {
  try {
    if (!token || token.trim() === '') {
      return true;
    }
    
    // JWT token formatı: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return true; // Geçersiz JWT formatı
    }
    
    // Payload'ı decode et (base64)
    const payload = JSON.parse(atob(parts[1]));
    
    // Expiry zamanını 5 dk tolerans ile kontrol et
    if (payload.exp) {
      const now = Date.now() / 1000;
      if (payload.exp + 300 < now) { // 5 dk tolerans
        return true; // Token süresi dolmuş
      }
    }
    
    return false; // Token geçerli
  } catch (error) {
    logger.error('Token decode hatası:', error);
    return true; // Hata varsa token'ı geçersiz kabul et
  }
}

// 401 hatası geldiğinde otomatik logout yap
function handleUnauthorized() {
  // Token'ı temizle
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('currentUser');
  
  // Custom event tetikle - App.tsx bu event'i dinleyip logout yapacak
  window.dispatchEvent(new CustomEvent('tokenExpired', { 
    detail: { message: 'Oturum süreniz doldu. Lütfen tekrar giriş yapın.' } 
  }));
}

// Generic API fetch fonksiyonu
async function fetchAPI(endpoint: string, options: RequestInit = {}) {
  // Login endpoint'leri için token gönderme (sadece POST /auth/login ve POST /admin/login)
  // POST /registrations yeni kayıt oluşturmak için (public endpoint, token gerekmez)
  // POST /registrations/{id}/approve, POST /registrations/{id}/reject admin token gerektirir
  // DELETE /registrations/{id} admin token gerektirir
  const isLoginEndpoint = (endpoint.includes('/auth/login') || endpoint.includes('/admin/login')) && options.method === 'POST';
  const isPublicRegistrationEndpoint = endpoint.includes('/registrations') && 
    options.method === 'POST' && 
    !endpoint.includes('/approve') && 
    !endpoint.includes('/reject') &&
    !endpoint.match(/\/registrations\/\d+$/); // Sadece POST /registrations (yeni kayıt) public
  
  // Login endpoint'leri için token gönderme (ama localStorage'dan temizleme yapma - login response'da kaydedilecek)
  // NOT: Login endpoint'lerinde token göndermiyoruz ama localStorage'dan da silmiyoruz
  // Çünkü login başarılı olduktan sonra token response'da gelecek ve kaydedilecek
  // Eğer önce temizlersek, login response'u gelene kadar localStorage boş kalır
  
  // Token'ı localStorage'dan al
  // - Login endpoint'leri için null (göndermiyoruz)
  // - Public registration endpoint'i için null (yeni kayıt, token gerekmez)
  // - Diğer tüm endpoint'ler için token gönder (admin/company işlemleri)
  const token = (isLoginEndpoint || isPublicRegistrationEndpoint) ? null : localStorage.getItem('authToken');
  
  // Debug: Token durumunu kontrol et (token gereken endpoint'ler için)
  if (!isLoginEndpoint && !isPublicRegistrationEndpoint) {
    const storedToken = localStorage.getItem('authToken');
    if (!storedToken) {
      logger.warn('⚠️ localStorage\'da authToken bulunamadı!');
      logger.warn('⚠️ localStorage içeriği:', Object.keys(localStorage).filter(key => key.includes('auth') || key.includes('token') || key.includes('user')));
    }
  }
  
  // Token varsa ve login/public endpoint değilse, token'ı backend'e gönder
  // JWT token'ları decode edip expiry kontrolü yapıyoruz
  if (token && !isLoginEndpoint && !isPublicRegistrationEndpoint) {
    // JWT token expiry kontrolü
    if (isTokenExpired(token)) {
      logger.warn('⚠️ Token süresi dolmuş, logout yapılıyor...');
      handleUnauthorized();
      throw new Error('Oturum süreniz doldu. Lütfen tekrar giriş yapın.');
    }
    logger.log('✅ Token geçerli, backend\'e gönderiliyor');
  }
  
  // Header'ları oluştur - Authorization header'ını en son ekle
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  
  // Token gönderme kuralları:
  // - Login endpoint'lerinde token gönderme (yeni giriş için eski token'a ihtiyaç yok)
  // - Public registration endpoint'inde token gönderme (yeni kayıt, token gerekmez)
  // - Diğer tüm endpoint'ler için token gönder (admin/company işlemleri)
  if (token && !isLoginEndpoint && !isPublicRegistrationEndpoint) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Timeout kontrolü için AbortController
  // Mobil cihazlarda daha uzun timeout (yavaş ağ bağlantıları için)
  // Admin panel için daha uzun timeout (çok veri çekiliyor)
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(navigator.userAgent.toLowerCase());
  const isAdminEndpoint = endpoint.includes('/admin/stats') || endpoint.includes('/companies');
  const timeoutDuration = isAdminEndpoint 
    ? 60000 // Admin endpoint'leri için 60 saniye
    : (isMobile ? 60000 : 45000); // Mobil: 60 saniye, Desktop: 45 saniye
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);

  const config: RequestInit = {
    ...options,
    headers: headers,
    signal: controller.signal,
  };

  // Debug için (production'da devre dışı - performans)
  if (token && !isLoginEndpoint && !isPublicRegistrationEndpoint) {
    logger.log('🔑 Token gönderiliyor:', token.substring(0, 20) + '...');
    logger.log('📡 Endpoint:', endpoint);
  } else if (!token && !isLoginEndpoint && !isPublicRegistrationEndpoint) {
    logger.warn('⚠️ Token bulunamadı! Login yapmanız gerekiyor.');
    logger.warn('📡 Endpoint:', endpoint);
  } else if (isLoginEndpoint) {
    logger.log('🔓 Login endpoint - token gönderilmiyor:', endpoint);
  } else if (isPublicRegistrationEndpoint) {
    logger.log('🔓 Public registration endpoint - token gönderilmiyor:', endpoint);
  }

  try {
    const fullUrl = `${API_BASE_URL}${endpoint}`;
    logger.log('🌐 API Request:', fullUrl, config.method || 'GET');
    logger.log('📱 Mobil cihaz:', isMobile ? 'Evet' : 'Hayır', '| Timeout:', timeoutDuration / 1000, 'saniye');
    
    const response = await fetch(fullUrl, config);
    clearTimeout(timeoutId); // Başarılı olursa timeout'u temizle
    logger.log('📡 API Response:', response.status, response.statusText, '| URL:', fullUrl);
    
    if (!response.ok) {
      let error;
      // Response'u clone edip hem JSON hem text olarak okuyabilmek için
      const responseClone = response.clone();
      try {
        const errorData = await response.json();
        error = errorData;
        console.log('❌ Error data (JSON):', errorData);
      } catch (parseError) {
        // JSON parse edilemezse, response text'i al
        try {
          const text = await responseClone.text();
          logger.log('❌ Error text:', text);
          error = { 
            message: text || `Sunucu hatası (${response.status})`,
            error: text,
            status: response.status 
          };
        } catch (textError) {
          error = { 
            message: `Sunucu hatası (${response.status})`,
            status: response.status 
          };
        }
      }
      
      // 401 hatası için otomatik logout (sadece login/public endpoint'leri değilse)
      if (response.status === 401) {
        // Login endpoint'leri için 401 normaldir (yanlış şifre/email), logout yapma
        if (isLoginEndpoint) {
          logger.log('ℹ️ Login endpoint - 401 hatası normal (yanlış şifre/email olabilir)');
          // Login endpoint'leri için sadece hata mesajını göster, logout yapma
          throw new Error(error.message || 'Giriş başarısız');
        }
        
        // Public registration endpoint'i için 401 normaldir (zaten token yok)
        if (isPublicRegistrationEndpoint) {
          logger.log('ℹ️ Public registration endpoint - 401 hatası beklenmedik');
          throw new Error(error.message || 'Kayıt başarısız');
        }
        
        // Diğer endpoint'ler için token kontrolü yap
        logger.error('❌ 401 Unauthorized - Token geçersiz veya eksik');
        logger.error('📡 Endpoint:', endpoint);
        logger.error('🔑 Token:', token ? 'Var (ama geçersiz)' : 'Yok');
        
        // Token varsa ama geçersizse logout yap
        if (token) {
          logger.warn('⚠️ Token geçersiz, logout yapılıyor...');
          handleUnauthorized();
        } else {
          logger.warn('⚠️ Token yok, giriş yapmanız gerekiyor');
        }
      }
      
      // 500 hatası için daha açıklayıcı mesaj
      if (response.status === 500) {
        logger.error('❌ Sunucu hatası:', error);
        throw new Error(error.message || 'Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyin.');
      }
      
      throw new Error(error.message || `API hatası (${response.status})`);
    }
    
    return await response.json();
  } catch (error: any) {
    clearTimeout(timeoutId); // Hata durumunda da timeout'u temizle
    logger.error('API Error:', error);
    
    // Timeout hatası kontrolü
    if (error.name === 'AbortError' || error.message?.includes('aborted')) {
      throw new Error('İstek zaman aşımına uğradı. Lütfen tekrar deneyin.');
    }
    
    // Network hatası kontrolü
    if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
      throw new Error('Ağ bağlantı hatası. İnternet bağlantınızı kontrol edin.');
    }
    
    // Eğer zaten Error objesi ise direkt fırlat, değilse yeni Error oluştur
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(error?.message || 'Bir hata oluştu');
  }
}

// Cari Hesap API'leri
export const cariHesapAPI = {
  getAll: () => fetchAPI('/cari-hesaplar'),
  getById: (id: number) => fetchAPI(`/cari-hesaplar/${id}`),
  create: (data: any) => fetchAPI('/cari-hesaplar', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) => fetchAPI(`/cari-hesaplar/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: number) => fetchAPI(`/cari-hesaplar/${id}`, { method: 'DELETE' }),
  getHareketler: (cariHesapId: number) => fetchAPI(`/cari-hesaplar/${cariHesapId}/hareketler`),
};

// Cari Hareket API'leri
export const cariHareketAPI = {
  create: (data: any) => fetchAPI('/cari-hareketler', { method: 'POST', body: JSON.stringify(data) }),
  getAll: (cashRegisterId?: number) => {
    const url = cashRegisterId ? `/cari-hareketler?cashRegisterId=${cashRegisterId}` : '/cari-hareketler';
    return fetchAPI(url);
  }, // Tüm işlemleri getir (isteğe bağlı cashRegisterId ile filtreleme)
  getByCariHesap: (cariHesapId: number) => fetchAPI(`/cari-hareketler?cariHesapId=${cariHesapId}`),
  delete: (id: number) => fetchAPI(`/cari-hareketler/${id}`, { method: 'DELETE' }),
};

// Ürün/Stok API'leri
export const urunAPI = {
  getAll: () => fetchAPI('/urunler'),
  getById: (id: number) => fetchAPI(`/urunler/${id}`),
  create: (data: any) => fetchAPI('/urunler', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) => fetchAPI(`/urunler/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: number) => fetchAPI(`/urunler/${id}`, { method: 'DELETE' }),
  search: (query: string) => fetchAPI(`/urunler/search?q=${encodeURIComponent(query)}`),
};

// Stok Hareket API'leri
export const stokHareketAPI = {
  create: (data: any) => fetchAPI('/stok-hareketler', { method: 'POST', body: JSON.stringify(data) }),
  getByUrun: (urunId: number) => fetchAPI(`/stok-hareketler?urunId=${urunId}`),
};

// Resim Yükleme API'leri
export const uploadAPI = {
  upload: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('image', file);
    
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Giriş yapmanız gerekiyor');
    }
    
    const response = await fetch(`${API_BASE_URL}/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        // Content-Type header'ını ekleme - FormData için browser otomatik ayarlar
      },
      body: formData,
    });
    
    if (!response.ok) {
      let error;
      try {
        error = await response.json();
      } catch (e) {
        error = { message: `HTTP ${response.status} hatası` };
      }
      throw new Error(error.message || 'Resim yüklenemedi');
    }
    
    return response.json();
  },
  delete: (fileName: string) => fetchAPI('/upload', { 
    method: 'DELETE', 
    body: JSON.stringify({ fileName }) 
  }),
};

// Kasa API'leri
export const kasaAPI = {
  getAll: () => fetchAPI('/kasalar'),
  getById: (id: number) => fetchAPI(`/kasalar/${id}`),
  create: (data: any) => fetchAPI('/kasalar', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) => fetchAPI(`/kasalar/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: number) => fetchAPI(`/kasalar/${id}`, { method: 'DELETE' }),
  transfer: (data: { fromCashRegisterId: number; toCashRegisterId: number; amount: number; description?: string; date?: string }) => 
    fetchAPI('/kasalar/transfer', { method: 'POST', body: JSON.stringify(data) }),
};

// Gelir-Gider API'leri
export const gelirGiderAPI = {
  getAll: () => fetchAPI('/gelir-giderler'),
  getById: (id: number) => fetchAPI(`/gelir-giderler/${id}`),
  create: (data: any) => fetchAPI('/gelir-giderler', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) => fetchAPI(`/gelir-giderler/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: number) => fetchAPI(`/gelir-giderler/${id}`, { method: 'DELETE' }),
};

// Satış API'leri (Cari-Stok Entegrasyonu)
export const satisAPI = {
  create: (data: any) => fetchAPI('/satislar', { method: 'POST', body: JSON.stringify(data) }),
  getAll: () => fetchAPI('/satislar'),
  getById: (id: number) => fetchAPI(`/satislar/${id}`),
};

// Dashboard API'leri
export const dashboardAPI = {
  getStats: () => fetchAPI('/dashboard/stats'),
  getRecentActivity: () => fetchAPI('/dashboard/activity'),
};

// Admin API'leri
export const adminAPI = {
  login: (email: string, password: string) => {
    logger.log('🔐 Admin Login Request - Email:', email.trim(), 'Password:', password ? '***' : 'EMPTY');
    // Şifreyi TRIM ETME - kullanıcı şifresinde başta/sonda boşluk olabilir
    return fetchAPI('/admin/login', { 
      method: 'POST', 
      body: JSON.stringify({ email: email.trim(), password: password }) 
    });
  },
  logout: () => fetchAPI('/admin/logout', { method: 'POST' }),
  getProfile: () => fetchAPI('/admin/profile'),
  updateProfile: (data: any) => 
    fetchAPI('/admin/profile', { 
      method: 'PUT', 
      body: JSON.stringify(data) 
    }),
  changePassword: (currentPassword: string, newPassword: string) => {
    // Şifreleri TRIM ETME - kullanıcı şifresinde başta/sonda boşluk olabilir
    return fetchAPI('/admin/change-password', { 
      method: 'PUT', 
      body: JSON.stringify({ currentPassword: currentPassword, newPassword: newPassword }) 
    });
  },
  listAdmins: (filters?: { search?: string; role?: string; status?: string; page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.role) params.append('role', filters.role);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    const query = params.toString();
    return fetchAPI(`/admin${query ? '?' + query : ''}`);
  },
  getStats: () => fetchAPI('/admin/stats'),
  getCompanyStats: (companyId: number) => fetchAPI(`/admin/stats/company/${companyId}`),
};

// Company API'leri
export const companyAPI = {
  login: (email: string, password: string) => {
    logger.log('🔐 Company Login Request - Email:', email, 'Password:', password ? '***' : 'EMPTY');
    return fetchAPI('/auth/login', { 
      method: 'POST', 
      body: JSON.stringify({ email: email.trim(), password: password.trim() }) 
    });
  },
  getAll: () => fetchAPI('/companies'), // Admin için tüm companies
};

// Registration API'leri
export const registrationAPI = {
  create: (data: any) => 
    fetchAPI('/registrations', { 
      method: 'POST', 
      body: JSON.stringify(data) 
    }),
  getAll: (status?: string, page?: number, limit?: number) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());
    const query = params.toString();
    return fetchAPI(`/registrations${query ? '?' + query : ''}`);
  },
  getById: (id: number) => fetchAPI(`/registrations/${id}`),
  approve: (id: number, data: any) => 
    fetchAPI(`/registrations/${id}/approve`, { 
      method: 'POST', 
      body: JSON.stringify(data) 
    }),
  reject: (id: number, reason: string) => 
    fetchAPI(`/registrations/${id}/reject`, { 
      method: 'POST', 
      body: JSON.stringify({ rejectionReason: reason }) 
    }),
  delete: (id: number) => fetchAPI(`/registrations/${id}`, { method: 'DELETE' }),
};

// Companies API (Admin için)
export const companiesAPI = {
  getAll: (filters?: { search?: string; status?: string; page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    const query = params.toString();
    return fetchAPI(`/companies${query ? '?' + query : ''}`);
  },
  getById: (id: number) => fetchAPI(`/companies/${id}`),
  update: (id: number, data: any) => 
    fetchAPI(`/companies/${id}`, { 
      method: 'PUT', 
      body: JSON.stringify(data) 
    }),
  updateStatus: (id: number, status: string) => 
    fetchAPI(`/companies/${id}/status`, { 
      method: 'PUT', 
      body: JSON.stringify({ status }) 
    }),
  extendLicense: (id: number, days: number) => 
    fetchAPI(`/companies/${id}`, { 
      method: 'PUT', 
      body: JSON.stringify({ extendLicenseDays: days }) 
    }),
  delete: (id: number) => fetchAPI(`/companies/${id}`, { method: 'DELETE' }),
};

// ==== Uyumlu Basit API Sarmalayıcıları ====

export const authAPI = {
  login: (email: string, password: string) =>
    fetchAPI('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
};

export const salaryAPI = {
  autoCharge: () => fetchAPI('/salary-auto-charge', { method: 'POST' }),
};

export const cariAPI = {
  list: () => fetchAPI('/cari-hesaplar'),
  create: (payload: any) => {
    logger.log('📤 cariAPI.create - Gönderilen payload:', JSON.stringify(payload, null, 2));
    logger.log('📤 cariAPI.create - payload.type:', payload.type, '| Tip:', typeof payload.type);
    const body = JSON.stringify(payload);
    logger.log('📤 cariAPI.create - JSON string:', body);
    return fetchAPI('/cari-hesaplar', { method: 'POST', body });
  },
  update: (id: number, payload: any) =>
    fetchAPI(`/cari-hesaplar/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  remove: (id: number) => fetchAPI(`/cari-hesaplar/${id}`, { method: 'DELETE' }),
  delete: (id: number) => fetchAPI(`/cari-hesaplar/${id}`, { method: 'DELETE' }),
};

export const cashAPI = {
  list: () => fetchAPI('/kasalar'),
  create: (payload: any) =>
    fetchAPI('/kasalar', { method: 'POST', body: JSON.stringify(payload) }),
  update: (id: number, payload: any) =>
    fetchAPI(`/kasalar/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  remove: (id: number) => fetchAPI(`/kasalar/${id}`, { method: 'DELETE' }),
  transfer: (data: { fromCashRegisterId: number; toCashRegisterId: number; amount: number; description?: string; date?: string }) =>
    fetchAPI('/kasalar/transfer', { method: 'POST', body: JSON.stringify(data) }),
};

export const incomeAPI = {
  list: (cashRegisterId?: number) => {
    const url = cashRegisterId ? `/gelir-giderler?cashRegisterId=${cashRegisterId}` : '/gelir-giderler';
    return fetchAPI(url);
  },
  create: (payload: any) =>
    fetchAPI('/gelir-giderler', { method: 'POST', body: JSON.stringify(payload) }),
  remove: (id: number) => fetchAPI(`/gelir-giderler/${id}`, { method: 'DELETE' }),
};

export const productAPI = {
  list: () => fetchAPI('/urunler'),
  create: (payload: any) =>
    fetchAPI('/urunler', { method: 'POST', body: JSON.stringify(payload) }),
  update: (id: number, payload: any) =>
    fetchAPI(`/urunler/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  remove: (id: number) => fetchAPI(`/urunler/${id}`, { method: 'DELETE' }),
};

// Teklif API
export const quotesAPI = {
  list: () => fetchAPI('/teklifler'),
  get: (id: number) => fetchAPI(`/teklifler/${id}`),
  create: (payload: any) =>
    fetchAPI('/teklifler', { method: 'POST', body: JSON.stringify(payload) }),
  update: (id: number, payload: any) =>
    fetchAPI(`/teklifler/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  remove: (id: number) => fetchAPI(`/teklifler/${id}`, { method: 'DELETE' }),
  getSettings: () => fetchAPI('/teklifler/settings'),
  updateSettings: (payload: any) =>
    fetchAPI('/teklifler/settings', { method: 'PUT', body: JSON.stringify(payload) }),
};

// Yazıcı ayarları (cihazlar arası senkron)
export const printerSettingsAPI = {
  get: () => fetchAPI('/printer-settings'),
  save: (payload: any) =>
    fetchAPI('/printer-settings', { method: 'PUT', body: JSON.stringify(payload) }),
};

export const purchaseRecordsAPI = {
  list: (productId?: number) => {
    const url = productId ? `/alış-kayıtları?productId=${productId}` : '/alış-kayıtları';
    return fetchAPI(url);
  },
  create: (payload: any) =>
    fetchAPI('/alış-kayıtları', { method: 'POST', body: JSON.stringify(payload) }),
  remove: (id: number) => fetchAPI(`/alış-kayıtları/${id}`, { method: 'DELETE' }),
};

export const salesAPI = {
  list: (productId?: number) => {
    const url = productId ? `/satislar?productId=${productId}` : '/satislar';
    return fetchAPI(url);
  },
  create: (payload: any) =>
    fetchAPI('/satislar', { method: 'POST', body: JSON.stringify(payload) }),
  remove: (id: number) => fetchAPI(`/satislar/${id}`, { method: 'DELETE' }),
};

// Profile API
export const ibanAPI = {
  list: (currentAccountId?: number) => {
    const url = currentAccountId ? `/ibans?currentAccountId=${currentAccountId}` : '/ibans';
    return fetchAPI(url);
  },
  create: (data: any) => fetchAPI('/ibans', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }),
  update: (id: number, data: any) => fetchAPI(`/ibans/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }),
  remove: (id: number) => fetchAPI(`/ibans/${id}`, {
    method: 'DELETE',
  }),
  delete: (id: number) => fetchAPI(`/ibans/${id}`, {
    method: 'DELETE',
  }),
};

export const profileAPI = {
  get: () => fetchAPI('/profile'),
  update: (data: any) =>
    fetchAPI('/profile', { method: 'PUT', body: JSON.stringify(data) }),
};

// Category API
export const checksAPI = {
  list: (filters?: { currentAccountId?: number; checkType?: string; status?: string }) => {
    const params = new URLSearchParams();
    if (filters?.currentAccountId) params.append('currentAccountId', filters.currentAccountId.toString());
    if (filters?.checkType) params.append('checkType', filters.checkType);
    if (filters?.status) params.append('status', filters.status);
    const query = params.toString();
    return fetchAPI(`/cekler${query ? '?' + query : ''}`);
  },
  getUpcoming: () => fetchAPI('/cekler?upcoming=true'),
  getById: (id: number) => fetchAPI(`/cekler/${id}`),
  create: (data: any) => fetchAPI('/cekler', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) => fetchAPI(`/cekler/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: number) => fetchAPI(`/cekler/${id}`, { method: 'DELETE' }),
};

export const categoryAPI = {
  list: () => fetchAPI('/kategoriler'),
  create: (payload: any) =>
    fetchAPI('/kategoriler', { method: 'POST', body: JSON.stringify(payload) }),
  update: (id: number, payload: any) =>
    fetchAPI(`/kategoriler/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  delete: (id: number) => fetchAPI(`/kategoriler/${id}`, { method: 'DELETE' }),
};


